
import { DCTOS_COLORS } from '@/lib/dctos-config';

// Generate realistic example scores for each profile type based on their behavioral pattern characteristics

export const generateExampleScores = (patternCode: string) => {
  // Handle HHLL sub-categories with specific factor relationships
  if (patternCode.startsWith('HHLL-')) {
    return generateHHLLSubCategoryScores(patternCode);
  }
  
  // Parse the pattern code to determine high/low for each factor
  const [d, i, s, c] = patternCode.split('');
  
  // Convert H/L to percentile ranges
  const getPercentileRange = (letter: string): [number, number] => {
    switch (letter) {
      case 'H': return [65, 85]; // High range
      case 'L': return [15, 35]; // Low range
      default: return [45, 55]; // Middle range for any other cases
    }
  };

  // Generate random value within range
  const generateValue = (range: [number, number]): number => {
    const [min, max] = range;
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const dRange = getPercentileRange(d);
  const iRange = getPercentileRange(i);
  const sRange = getPercentileRange(s);
  const cRange = getPercentileRange(c);

  return [
    {
      name: 'D',
      fullName: 'Drive',
      value: generateValue(dRange),
      color: DCTOS_COLORS.drive
    },
    {
      name: 'C',
      fullName: 'Connection',
      value: generateValue(iRange),
      color: DCTOS_COLORS.connection
    },
    {
      name: 'T',
      fullName: 'Task',
      value: generateValue(sRange),
      color: DCTOS_COLORS.task
    },
    {
      name: 'O',
      fullName: 'Order',
      value: generateValue(cRange),
      color: DCTOS_COLORS.order
    }
  ];
};

// Generate scores for HHLL sub-categories with specific factor relationships
const generateHHLLSubCategoryScores = (patternCode: string) => {
  // Generate base random values with proper ordering
  const generateOrderedPair = (higherRange: [number, number], lowerRange: [number, number]): [number, number] => {
    const higher = Math.floor(Math.random() * (higherRange[1] - higherRange[0] + 1)) + higherRange[0];
    const lower = Math.floor(Math.random() * (lowerRange[1] - lowerRange[0] + 1)) + lowerRange[0];
    return [higher, lower];
  };

  let dScore: number, cScore: number, tScore: number, oScore: number;

  switch (patternCode) {
    case 'HHLL-AHDH': {
      // D>C (both high) and O>T (both low)
      const [d, c] = generateOrderedPair([75, 85], [65, 74]);
      const [o, t] = generateOrderedPair([25, 35], [15, 24]);
      dScore = d; cScore = c; tScore = t; oScore = o;
      break;
    }
    case 'HHLL-AHDL': {
      // D>C (both high) and T>O (both low)
      const [d, c] = generateOrderedPair([75, 85], [65, 74]);
      const [t, o] = generateOrderedPair([25, 35], [15, 24]);
      dScore = d; cScore = c; tScore = t; oScore = o;
      break;
    }
    case 'HHLL-BHDH': {
      // C>D (both high) and O>T (both low)
      const [c, d] = generateOrderedPair([75, 85], [65, 74]);
      const [o, t] = generateOrderedPair([25, 35], [15, 24]);
      dScore = d; cScore = c; tScore = t; oScore = o;
      break;
    }
    case 'HHLL-BHDL': {
      // C>D (both high) and T>O (both low)
      const [c, d] = generateOrderedPair([75, 85], [65, 74]);
      const [t, o] = generateOrderedPair([25, 35], [15, 24]);
      dScore = d; cScore = c; tScore = t; oScore = o;
      break;
    }
    default: {
      // Fallback to regular HHLL pattern
      dScore = Math.floor(Math.random() * 21) + 65; // 65-85
      cScore = Math.floor(Math.random() * 21) + 65; // 65-85
      tScore = Math.floor(Math.random() * 21) + 15; // 15-35
      oScore = Math.floor(Math.random() * 21) + 15; // 15-35
      break;
    }
  }

  return [
    {
      name: 'D',
      fullName: 'Drive',
      value: dScore,
      color: DCTOS_COLORS.drive
    },
    {
      name: 'C',
      fullName: 'Connection',
      value: cScore,
      color: DCTOS_COLORS.connection
    },
    {
      name: 'T',
      fullName: 'Task',
      value: tScore,
      color: DCTOS_COLORS.task
    },
    {
      name: 'O',
      fullName: 'Order',
      value: oScore,
      color: DCTOS_COLORS.order
    }
  ];
};
